import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { propertyAPI } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { ArrowRight } from 'lucide-react';

/**
 * Curated pool of 14 bohemian / cozy hero image pairs — one for every day of a
 * two-week cycle. The homepage picks a pair deterministically based on the day
 * of the year, so every visitor sees the same hero on any given day and it
 * swaps automatically at local midnight. Rolls over cleanly every 14 days.
 * Each entry: [large arch image, small overlapping thumb].
 */
const HERO_POOL = [
  [
    'https://images.unsplash.com/photo-1632119580908-ae947d4c7691?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1551927411-95e412943b58?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1570793005386-840846445fed?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1552558636-f6a8f071c2b3?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1519974719765-e6559eac2575?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1588854337221-4cf9fa96059c?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1567016432779-094069958ea5?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1595429035839-c99c298ffdde?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1541123437800-1bb1317badc2?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1584589167171-541ce45f1eea?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1596178065887-1198b6148b2b?auto=format&fit=crop&w=600&q=80',
  ],
  [
    'https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1560184897-ae75f418493e?auto=format&fit=crop&w=600&q=80',
  ],
];

/** Deterministic day-of-year index — same image for every visitor per day. */
const getDailyHeroPair = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return HERO_POOL[dayOfYear % HERO_POOL.length];
};

const Homepage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroMain, heroSecondary] = useMemo(() => getDailyHeroPair(), []);

  useEffect(() => {
    propertyAPI
      .list()
      .then(setProperties)
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = properties.slice(0, 6);

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Navbar />

      {/* Hero — asymmetric split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 space-y-8">
            <span className="inline-block text-xs uppercase tracking-[0.24em] text-primary font-medium">
              Curated stays · handpicked
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              A quiet collection of{' '}
              <span className="italic text-primary">unhurried</span> stays.
            </h1>
            <p className="text-lg text-muted-foreground font-light leading-relaxed max-w-lg">
              From cozy stays to crazy nights — we host it all. Every property below is
              tasted, tested and taken care of. Click through to book on airbnb.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <Link to="/properties">
                <button
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary text-primary-foreground text-sm font-medium tracking-wide transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_18px_40px_-12px_rgba(179,74,62,0.45)]"
                  data-testid="hero-cta-btn"
                >
                  Explore properties
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <a
                href="#latest"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                or scroll to browse ↓
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            {/* Arch main image */}
            <div className="relative aspect-[4/5] w-full max-w-md ml-auto rounded-t-[280px] rounded-b-3xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(45,42,38,0.35)]">
              <img
                src={heroMain}
                alt="A cozy bohemian living room"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Small overlapping image */}
            <div className="hidden md:block absolute -bottom-8 -left-4 lg:-left-10 w-44 lg:w-56 aspect-square rounded-3xl overflow-hidden border-4 border-background shadow-[0_20px_40px_-15px_rgba(45,42,38,0.35)]">
              <img
                src={heroSecondary}
                alt="A-frame cabin escape"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Latest listings — bento grid */}
      <section
        id="latest"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32"
      >
        <div className="flex items-end justify-between mb-10 lg:mb-14">
          <div>
            <span className="block text-xs uppercase tracking-[0.24em] text-primary font-medium mb-3">
              The Collection
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl text-foreground">
              Latest listings
            </h2>
            <p className="text-muted-foreground mt-3 font-light">
              {loading
                ? 'Loading...'
                : `${properties.length} propert${properties.length === 1 ? 'y' : 'ies'} in the collection.`}
            </p>
          </div>
          <Link
            to="/properties"
            className="text-sm text-primary hover:opacity-70 transition-opacity hidden sm:inline-flex items-center gap-1"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            <div className="col-span-12 md:col-span-8 aspect-[4/3] bg-muted rounded-3xl animate-pulse" />
            <div className="col-span-12 md:col-span-4 aspect-square bg-muted rounded-3xl animate-pulse" />
            <div className="col-span-12 md:col-span-4 aspect-[3/4] bg-muted rounded-3xl animate-pulse" />
            <div className="col-span-12 md:col-span-8 aspect-video bg-muted rounded-3xl animate-pulse" />
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-border rounded-3xl">
            <p className="text-muted-foreground">
              No properties in the collection yet. Come back soon.
            </p>
          </div>
        ) : (
          <BentoGrid items={featured} />
        )}
      </section>

      <Footer />
    </div>
  );
};

/**
 * Bento layout — cycles a 4-tile pattern that varies aspect ratio and column
 * span, giving the grid a gallery-wall rhythm instead of the usual spreadsheet.
 */
const BENTO_PATTERN = [
  { cols: 'md:col-span-8', aspect: 'aspect-[4/3]' },
  { cols: 'md:col-span-4', aspect: 'aspect-square' },
  { cols: 'md:col-span-4', aspect: 'aspect-[3/4]' },
  { cols: 'md:col-span-8', aspect: 'aspect-video' },
];

const BentoGrid = ({ items }) => (
  <div className="grid grid-cols-12 gap-4 lg:gap-6">
    {items.map((p, i) => {
      const slot = BENTO_PATTERN[i % BENTO_PATTERN.length];
      return (
        <div
          key={p.id}
          className={`col-span-12 ${slot.cols}`}
        >
          <PropertyCard property={p} aspect={slot.aspect} />
        </div>
      );
    })}
  </div>
);

export default Homepage;
